/**
 * Issue and withdraw external share links (CLAUDE.md 11.4).
 *
 * The dialog exists to make two facts unmissable, because getting either wrong is how
 * a link leaks:
 *
 * 1. **The token is shown once.** The server kept only a hash, so there is no "show
 *    link" action to reach for tomorrow. The freshly minted link therefore appears in
 *    its own panel, above the list, with a copy button -- not as a row that looks like
 *    the others.
 * 2. **A link is a credential.** Whoever holds it can read the calculation without
 *    signing in, so the list leads with expiry, whether a password is set, and how
 *    many times it has been opened -- and revoking is one click away.
 */
import { useState, type FormEvent } from 'react'
import { Check, Copy, Link2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useCreateShareLink, useRevokeShareLink, useShareLinks } from '@/api/hooks'
import type { ShareLink } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'
import { formatDateTime } from '@/lib/format'

export function ShareDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { data: links } = useShareLinks(projectId, open)
  const [minted, setMinted] = useState<ShareLink | null>(null)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // The token is unrecoverable once this closes, so it does not survive the
        // dialog -- leaving it on screen would suggest it could be found again.
        if (!next) setMinted(null)
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('share.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-text-muted">{t('share.intro')}</p>

          {minted && <MintedLink link={minted} />}

          <CreateForm projectId={projectId} onCreated={setMinted} />

          <LinkList projectId={projectId} links={links ?? []} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

function MintedLink({ link }: { link: ShareLink }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    if (!link.url) return
    try {
      await navigator.clipboard.writeText(link.url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can be refused; the link is selectable in the field either
      // way, so this is not worth an error toast.
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-accent bg-surface-raised p-3">
      <p className="text-sm font-medium">{t('share.copy_now')}</p>
      <p className="text-xs text-text-muted">{t('share.copy_now_hint')}</p>
      <div className="flex gap-2">
        <input
          readOnly
          value={link.url ?? ''}
          aria-label={t('share.url_label')}
          onFocus={(event) => event.target.select()}
          className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1 text-xs"
        />
        <Button size="sm" onClick={copy} aria-label={t('action.copy')}>
          {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
        </Button>
      </div>
    </div>
  )
}

function CreateForm({
  projectId,
  onCreated,
}: {
  projectId: number
  onCreated: (link: ShareLink) => void
}) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const createLink = useCreateShareLink(projectId)

  const [label, setLabel] = useState('')
  const [password, setPassword] = useState('')
  const [days, setDays] = useState('30')
  const [pending, setPending] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setPending(true)
    try {
      const link = await createLink.mutateAsync({
        scope: 'project',
        project_id: projectId,
        label: label.trim() || undefined,
        password: password || undefined,
        expires_days: Number(days) || undefined,
      })
      onCreated(link)
      setLabel('')
      setPassword('')
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    } finally {
      setPending(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 border-t border-border pt-4">
      <Field>
        <FieldLabel>{t('share.label')}</FieldLabel>
        <FieldInput
          value={label}
          placeholder={t('share.label_placeholder')}
          onChange={(event) => setLabel(event.target.value)}
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field>
          <FieldLabel>{t('share.expiry_days')}</FieldLabel>
          <FieldInput
            type="number"
            inputMode="numeric"
            min={1}
            max={730}
            value={days}
            onChange={(event) => setDays(event.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel>{t('share.password_optional')}</FieldLabel>
          <FieldInput
            type="password"
            autoComplete="new-password"
            minLength={6}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
      </div>

      <Button type="submit" variant="primary" loading={pending}>
        <Link2 size={16} aria-hidden />
        {t('share.create')}
      </Button>
    </form>
  )
}

function LinkList({ projectId, links }: { projectId: number; links: ShareLink[] }) {
  const { t, i18n } = useTranslation()
  const revoke = useRevokeShareLink(projectId)

  if (links.length === 0) {
    return <p className="text-sm text-text-muted">{t('share.none')}</p>
  }

  return (
    <ul className="space-y-2 border-t border-border pt-4">
      {links.map((link) => (
        <li
          key={link.id}
          className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2"
        >
          <span className="min-w-0 flex-1 truncate text-sm">
            {link.label || t(`share.scope.${link.scope}`)}
          </span>

          {link.requires_password && (
            <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs text-text-muted">
              {t('share.protected')}
            </span>
          )}

          <span className="text-xs text-text-muted">
            {link.active
              ? t('share.expires', {
                  date: formatDateTime(link.expires_at, i18n.language),
                })
              : t('share.inactive')}
          </span>

          <span className="tabular text-xs text-text-muted">
            {t('share.opened', { count: link.access_count })}
          </span>

          {link.active && (
            <Button
              size="sm"
              variant="ghost"
              aria-label={t('share.revoke')}
              onClick={() => revoke.mutate(link.id)}
            >
              <Trash2 size={16} aria-hidden />
            </Button>
          )}
        </li>
      ))}
    </ul>
  )
}
